import { localizedStrings } from '@core/strings'
import { Exclude, plainToInstance, Type } from 'class-transformer'
import { IRegisterItem } from './IRegisterItem'
import {
  ChecklistStatusCounts,
  PermitStatusCounts,
  RegisterInspectionConfigsByType,
  RegisterItemChecklistProperty,
  RegisterItemDiagramProperty,
  RegisterItemFlatPropertyData,
  RegisterItemInspectionProperty,
  RegisterItemPermitProperty,
  RegisterItemSelectProperty,
  RegisterItemStatus,
  RegisterItemStringProperty,
  RegisterItemTaskProperty,
  RegisterItemWithLinksPropertyData,
  TaskStatusCounts,
  RegisterItemAttachmentProperty,
} from './types'
import { Entity } from '@core/entities/entity'
import { RegisterTemplateProperty } from '@core/entities/register/RegisterTemplateProperty'
import moment from 'moment/moment'
import {
  AllShapes,
  GroupMetadata,
  ShapeGroup,
  SkiaOval,
  SkiaPolygon,
  SkiaRect,
} from '@core/entities/skia'
import { RegisterTemplate } from '@core/entities/register/RegisterTemplate'
import { palette } from '@core/theme/palette'
import { Permit } from '@core/entities/permit/Permit'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import { Register } from '@core/entities/register/Register/Register'
import { Task } from '@core/entities/task/Task'
import { TaskStatus } from '@core/entities/task/TaskBase'
import { ChecklistStatus } from '@core/entities/checklist/Checklist/types'
import {
  RegisterInspection,
  ScheduledInspection,
} from '@core/entities/register/RegisterInspection'

export class RegisterItem extends Entity<IRegisterItem> {
  id: number

  uniqueId: string

  data: RegisterItemFlatPropertyData[]

  dataWithLinkProperties?: RegisterItemWithLinksPropertyData[]

  status: RegisterItemStatus

  @Type(() => Register)
  register?: Register

  registerId?: number

  @Type(() => RegisterTemplateProperty)
  registerProperties: RegisterTemplateProperty[]

  @Exclude({ toPlainOnly: true })
  isSelected: boolean

  public static new(payload: unknown): RegisterItem {
    const entity = plainToInstance(RegisterItem, payload)

    return entity
  }

  public static getTaskStatusBgColor(status: TaskStatus): string {
    return `${Task.getStatusChipColor(status)}.main`
  }

  public selectRegisterItemList(): void {
    this.isSelected = true
  }

  public getRegisterItemPropertyDisplayValues(
    registerItemProperty: RegisterItemFlatPropertyData,
  ): string {
    return (
      this.getRegisterItemPropertyValues(registerItemProperty) ??
      localizedStrings.na
    )
  }

  public getRegisterItemPropertyValues(
    registerItemProperty: RegisterItemFlatPropertyData,
  ): string {
    switch (registerItemProperty.type) {
      default:
      case 'STRING':
        return registerItemProperty?.textValue
      case 'SELECT':
        return registerItemProperty?.selectedOption?.label
      case 'DATE':
        const dateRange = registerItemProperty?.dateValue
          ? moment(registerItemProperty?.dateValue).format('D MMM YYYY')
          : null
        const startDate = registerItemProperty?.startTime
          ? moment(registerItemProperty?.startTime).format('D MMM YYYY')
          : null
        const endDate = registerItemProperty?.endTime
          ? moment(registerItemProperty?.endTime).format('D MMM YYYY')
          : null
        return dateRange || startDate || endDate
      case 'NUMBER':
        return typeof registerItemProperty?.numberValue === 'number'
          ? String(registerItemProperty?.numberValue)
          : null
      case 'USER':
        return registerItemProperty?.selectedUser?.id
          ? String(
              `${registerItemProperty?.selectedUser?.firstName} ${registerItemProperty?.selectedUser?.lastName}`,
            )
          : null
      case 'DIAGRAM':
        return registerItemProperty?.siteBoxDocumentName
      case 'INSPECTION':
        const nextInspectionDue = registerItemProperty?.inspection
          ?.nextInspectionDue
          ? moment(registerItemProperty.inspection?.nextInspectionDue).format(
              'DD MMM YYYY HH:mm',
            )
          : null
        const lastInspectionDue = registerItemProperty?.inspection
          ?.lastInspectionTime
          ? moment(registerItemProperty.inspection?.lastInspectionTime).format(
              'DD MMM YYYY HH:mm',
            )
          : null
        return nextInspectionDue || lastInspectionDue
      case 'TIME':
        return registerItemProperty?.timeValue
      case 'PERMIT':
      case 'TASK':
      case 'CHECKLIST':
      case 'ATTACHMENT':
        return registerItemProperty?.value
    }
  }

  public getRegisterItemColor(colorPropertyId: string): string {
    const colorProperty = this.data.find(
      (property) => property.registerPropertyId === colorPropertyId,
    ) as RegisterItemSelectProperty<string>

    return colorProperty?.selectedOption?.colour
  }

  public getRegisterItemGroupMetadata(
    registerTemplate: RegisterTemplate,
  ): GroupMetadata {
    // Handling groupMetadata
    const colorPropertyId = registerTemplate.diagram.colourProperty.id
    const identifierPropertyId = registerTemplate.diagram.identifierProperty.id
    const colorRgb = this.getRegisterItemColor(colorPropertyId)
    const labels = registerTemplate.getDiagramLabels(this)

    return {
      color: colorRgb,
      groupId: this.uniqueId,
      labels: labels.map((label) => ({
        name: label.name,
        text: label.label,
        backgroundColor: label.color,
        enabled: true,
      })),
      groupName: this.getRegisterItemPropertyDisplayValues(
        this.data.find(
          (column) => column.registerPropertyId === identifierPropertyId,
        ) as RegisterItemStringProperty<string>,
      ),
    }
  }

  public getRegisterItemShapes(transformData?: {
    widthPixelsPerPoint: number
    heightPixelsPerPoint: number
  }): ShapeGroup {
    // Handling shapeGroups
    const itemAnnotations = (
      this.data.find(
        (column) => column.registerPropertyId === '-1',
      ) as RegisterItemDiagramProperty<string>
    ).annotations

    const shapes = itemAnnotations.map((annotation) => {
      switch (annotation.annotationData.type) {
        case 'POLYGON':
          if (transformData) {
            return SkiaPolygon.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
            )
          } else {
            return SkiaPolygon.newFromSiteboxDocumentAnnotation(annotation)
          }
        case 'RECTANGLE':
          if (transformData) {
            return SkiaRect.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
            )
          } else {
            return SkiaRect.newFromSiteboxDocumentAnnotation(annotation)
          }
        case 'ELLIPSE':
          if (transformData) {
            return SkiaOval.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
            )
          } else {
            return SkiaOval.newFromSiteboxDocumentAnnotation(annotation)
          }
        default:
          return null
      }
    })

    return { shapes, groupId: this.uniqueId }
  }

  public getRegisterItemShapesWithMetadata(
    registerTemplate: RegisterTemplate,
    transformData?: {
      widthPixelsPerPoint: number
      heightPixelsPerPoint: number
    },
  ): AllShapes[] {
    const colorPropertyId = registerTemplate.diagram.colourProperty.id
    const identifierPropertyId = registerTemplate.diagram.identifierProperty.id
    const colorRgb = this.getRegisterItemColor(colorPropertyId)
    const labels = registerTemplate.getDiagramLabels(this)

    let editLabels = []

    // Get the annotations from the register item
    const itemAnnotations = (
      this.data.find(
        (column) => column.registerPropertyId === '-1',
      ) as RegisterItemDiagramProperty<string>
    ).annotations

    const property = this.data.find(
      (d) => d.registerPropertyId === identifierPropertyId,
    )
    const propertyValue = this.getRegisterItemPropertyValues(property)

    editLabels = [
      {
        name: property.name,
        text: propertyValue,
        enabled: true,
      },
    ]

    const linkedPermitsStatuses = this.getActivePermitsStatusCountArray().map(
      ({ status, count }) => {
        const getHexColorFromTheme = (status: PermitStatus) => {
          const themeColorKey = Permit.getStatusChipColor(status)
          const color = palette[themeColorKey]
          return color ? color.main : '#000'
        }
        return {
          name: status,
          text: `${Permit.getStatusString(status)} (${count})`,
          backgroundColor: getHexColorFromTheme(status),
          withRadius: true,
          enabled: true,
        }
      },
    )

    // Get the metadata for the register item
    const metadata = {
      color: colorRgb,
      groupId: this.uniqueId,
      labels: labels.map((label) => ({
        name: label.name,
        text: label.label,
        backgroundColor: label.color,
        enabled: true,
      })),
      editLabels: editLabels,
      linkedPermitsStatuses: linkedPermitsStatuses,
      name: this.getRegisterItemPropertyDisplayValues(
        this.data.find(
          (column) => column.registerPropertyId === identifierPropertyId,
        ) as RegisterItemStringProperty<string>,
      ),
    }

    // Return Skia shape entities combining the shape data with metadata
    const shapes = itemAnnotations.map((annotation) => {
      switch (annotation.annotationData.type) {
        case 'POLYGON':
          if (transformData) {
            return SkiaPolygon.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
              metadata,
            )
          } else {
            return SkiaPolygon.newFromSiteboxDocumentAnnotation(
              annotation,
              metadata,
            )
          }
        case 'RECTANGLE':
          if (transformData) {
            return SkiaRect.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
              metadata,
            )
          } else {
            return SkiaRect.newFromSiteboxDocumentAnnotation(
              annotation,
              metadata,
            )
          }
        case 'ELLIPSE':
          if (transformData) {
            return SkiaOval.newFromSiteboxDocumentAnnotationImage(
              annotation,
              transformData,
              metadata,
            )
          } else {
            return SkiaOval.newFromSiteboxDocumentAnnotation(
              annotation,
              metadata,
            )
          }
        default:
          return null
      }
    })

    return shapes
  }

  public getRegisterItemPreviewData(
    previewProperties: number,
    hideTasks: boolean = false,
    data: RegisterItemFlatPropertyData[] = this.data,
  ): RegisterItemFlatPropertyData[] {
    return (
      data
        .filter(
          (item) =>
            item.type !== 'INSPECTION' &&
            (hideTasks ? item.type !== 'TASK' : true),
        )
        .slice(0, previewProperties) ?? []
    )
  }

  public isUniqueIdProperty(
    property: RegisterItemFlatPropertyData | RegisterItemWithLinksPropertyData,
  ): boolean {
    return property.registerPropertyId === '0'
  }

  public isDescriptionProperty(
    property: RegisterItemFlatPropertyData | RegisterItemWithLinksPropertyData,
  ): boolean {
    return !!property.isDescription
  }

  public isHeaderProperty(
    property: RegisterItemFlatPropertyData | RegisterItemWithLinksPropertyData,
  ): boolean {
    return (
      this.isUniqueIdProperty(property) || this.isDescriptionProperty(property)
    )
  }

  public getDescriptionProperty(): RegisterItemFlatPropertyData | undefined {
    return this.data.find((property) => this.isDescriptionProperty(property))
  }

  public getDescriptionPropertyText(): string | undefined {
    const property = this.getDescriptionProperty()
    const value = property
      ? this.getRegisterItemPropertyValues(property)?.trim()
      : undefined
    return value || undefined
  }

  public hasInspection(): boolean {
    return this.data.some(
      (item) =>
        item.type === 'INSPECTION' && !!item.inspection?.lastInspectedBy,
    )
  }

  public getActivePermits() {
    return this.data
      .filter((item) => item.type === 'PERMIT')
      .flatMap(
        (item) =>
          (item as RegisterItemPermitProperty<string>).activePermits || [],
      )
  }

  public getActiveTasks() {
    return this.data
      .filter((item) => item.type === 'TASK')
      .flatMap(
        (item) => (item as RegisterItemTaskProperty<string>).activeTasks || [],
      )
  }

  public getChecklists() {
    return this.data
      .filter((item) => item.type === 'CHECKLIST')
      .flatMap(
        (item) =>
          (item as RegisterItemChecklistProperty<string>).checklists || [],
      )
  }

  public getPermitTotalsSum(): number {
    return this.data.reduce((total, property) => {
      if (property.type === 'PERMIT') {
        return (
          total + (property as RegisterItemPermitProperty<string>).totalPermits
        )
      }
      return total
    }, 0)
  }

  public getTaskTotalsSum(): number {
    return this.data.reduce((total, property) => {
      if (property.type === 'TASK') {
        return total + (property as RegisterItemTaskProperty<string>).totalTasks
      }
      return total
    }, 0)
  }

  public getChecklistTotalsSum(): number {
    return this.data.reduce((total, property) => {
      if (property.type === 'CHECKLIST') {
        return (
          total +
          (property as RegisterItemChecklistProperty<string>).totalChecklists
        )
      }
      return total
    }, 0)
  }

  public getChecklistsStatusCountArray(): Array<ChecklistStatusCounts> {
    const statusMap = new Map<ChecklistStatus, number>()
    const checklists = this.getChecklists()
    for (const checklist of checklists) {
      const status = checklist.status
      if (status) {
        statusMap.set(status, (statusMap.get(status) || 0) + 1)
      }
    }
    return Array.from(statusMap.entries()).map(([status, count]) => ({
      status,
      count,
    }))
  }

  public getActivePermitsStatusCountArray(): Array<PermitStatusCounts> {
    const statusMap = new Map<PermitStatus, number>()
    const activePermits = this.getActivePermits()
    for (const permit of activePermits) {
      const status = permit.status
      if (status) {
        statusMap.set(status, (statusMap.get(status) || 0) + 1)
      }
    }
    return Array.from(statusMap.entries()).map(([status, count]) => ({
      status,
      count,
    }))
  }

  public getActiveTasksStatusCountArray(): Array<TaskStatusCounts> {
    const statusMap = new Map<TaskStatusCounts['status'], number>()

    const activeTasks = this.getActiveTasks()
    for (const task of activeTasks) {
      const taskEntity = Task.new(task)
      const status = task.status
      const isOverdue = taskEntity.isOverdue()

      if (status) {
        const key = isOverdue ? 'OVERDUE' : status
        statusMap.set(key, (statusMap.get(key) || 0) + 1)
      }
    }

    return Array.from(statusMap.entries()).map(([status, count]) => ({
      status,
      count,
    }))
  }

  public getPreviewPropertiesData(
    previewPropertyIds: Array<string>,
    maxProperties: number,
    highlight?: string,
  ): RegisterItemFlatPropertyData[] {
    // Header properties render in the card header, never as rows
    const rowProperties = this.data.filter(
      (property) => !this.isHeaderProperty(property),
    )

    const templatePreviewProperties = rowProperties.filter(
      (item) => item.isUsedForPreview,
    )

    const linkItemPreviewProperties = rowProperties.filter((i) =>
      previewPropertyIds?.some((id) => id === i.registerPropertyId),
    )

    const excludeTemplateDuplicates = (
      property: RegisterItemFlatPropertyData,
    ) => {
      return !templatePreviewProperties.some(
        (p) => p.registerPropertyId === property.registerPropertyId,
      )
    }

    const previewProperties = [
      ...templatePreviewProperties,
      ...linkItemPreviewProperties.filter(excludeTemplateDuplicates),
    ]

    const baseProperties =
      previewProperties.length > 0
        ? previewProperties
        : this.getRegisterItemPreviewData(maxProperties, false, rowProperties)

    if (!highlight) {
      return baseProperties
    }

    // highlight is true, return hidden properties that match the highlight term
    const matchingProperties = rowProperties.filter((property) => {
      const displayValue = this.getRegisterItemPropertyDisplayValues(property)
      return displayValue?.toLowerCase().includes(highlight.toLowerCase())
    })
    const propertyIds = baseProperties.map((p) => p.registerPropertyId)
    const additionalProperties = matchingProperties.filter(
      (property) => !propertyIds.includes(property.registerPropertyId),
    )

    return [...baseProperties, ...additionalProperties]
  }

  public isUrl(registerItemProperty: RegisterItemFlatPropertyData): boolean {
    const propertyValue =
      this.getRegisterItemPropertyValues(registerItemProperty)
    return (
      registerItemProperty.type === 'STRING' &&
      propertyValue?.startsWith('http')
    )
  }

  public getScheduleInspectionConfigs(): RegisterItemInspectionProperty<string>[] {
    return this.data.filter(
      (data) => data.type === 'INSPECTION',
    ) as RegisterItemInspectionProperty<string>[]
  }

  public getInspectionConfigsByType(): RegisterInspectionConfigsByType {
    const inspectionConfigs: RegisterItemInspectionProperty<string>[] = []
    const inactiveRows: RegisterItemInspectionProperty<string>[] = []

    for (const config of this.getScheduleInspectionConfigs()) {
      if (config.inspection) {
        inspectionConfigs.push({
          ...config,
          inspection: RegisterInspection.new(config.inspection),
        })
        continue
      }

      const activeScheduled = (config.scheduledInspections ?? []).filter(
        (s) => s.scheduleConfig?.isActive !== false,
      )
      const inactiveScheduled = (config.scheduledInspections ?? []).filter(
        (s) => s.scheduleConfig?.isActive === false,
      )

      if (activeScheduled.length) {
        inspectionConfigs.push({
          ...config,
          scheduledInspections: activeScheduled.map((raw) =>
            ScheduledInspection.new(raw),
          ),
        })
      }
      if (inactiveScheduled.length) {
        inactiveRows.push({
          ...config,
          scheduledInspections: inactiveScheduled.map((raw) =>
            ScheduledInspection.new(raw),
          ),
        })
      }
    }

    return { inspectionConfigs, inactiveRows }
  }

  public getInspectionConfigById(
    propertyId: string,
  ): RegisterItemInspectionProperty<string> | undefined {
    const configs = this.getInspectionConfigsByType()
    return (
      configs.inspectionConfigs.find(
        (config) => config.registerPropertyId === propertyId,
      ) ||
      configs.inactiveRows.find(
        (config) => config.registerPropertyId === propertyId,
      )
    )
  }

  public getScheduledInspectionById(
    id: number,
  ): ScheduledInspection | undefined {
    const configs = this.getInspectionConfigsByType()
    const activeSchedules = configs.inspectionConfigs.find(
      (c) =>
        Array.isArray(c.scheduledInspections) &&
        c.scheduledInspections.some((s) => s.id === id),
    )
    const inactiveSchedules = configs.inactiveRows.find((c) =>
      c.scheduledInspections?.some((s) => s.id === id),
    )
    return (activeSchedules || inactiveSchedules)?.scheduledInspections?.find(
      (s) => s.id === id,
    )
  }

  public getActiveInspectionsCount(): number {
    let count = 0
    const scheduleInspectionConfigs = this.getScheduleInspectionConfigs()
    for (const config of scheduleInspectionConfigs) {
      if (config.inspection) {
        count++
      }
      if (config.scheduledInspections) {
        config.scheduledInspections.forEach((scheduledInspection) => {
          if (scheduledInspection.scheduleConfig?.isActive !== false) {
            count++
          }
        })
      }
    }
    return count
  }

  public getAllInspectionsCount(): number {
    return this.data.filter((item) => item.type === 'INSPECTION').length
  }

  public getTotalAttachmentCount(): number {
    return this.data.reduce(
      (count, property) =>
        property.type === 'ATTACHMENT'
          ? count + (property.totalAttachments ?? 0)
          : count,
      0,
    )
  }

  public getOverdueInspectionsCount(): number {
    let count = 0
    const scheduleInspectionConfigs = this.getScheduleInspectionConfigs()
    for (const config of scheduleInspectionConfigs) {
      if (
        config.inspection?.nextInspectionDue &&
        new Date() > new Date(config.inspection?.nextInspectionDue as string)
      ) {
        count++
      }
      if (config.scheduledInspections) {
        config.scheduledInspections.forEach((scheduledInspection) => {
          if (
            scheduledInspection.scheduleConfig?.isActive !== false &&
            scheduledInspection.nextInspectionDue &&
            new Date() >
              new Date(scheduledInspection.nextInspectionDue as string)
          ) {
            count++
          }
        })
      }
    }
    return count
  }

  public haveStatusSummary(): boolean {
    return (
      this.getPermitTotalsSum() > 0 ||
      this.getActiveInspectionsCount() > 0 ||
      this.getChecklistTotalsSum() > 0 ||
      this.getTaskTotalsSum() > 0
    )
  }

  public getAttachmentProperties(): RegisterItemAttachmentProperty<string>[] {
    return this.data.filter(
      (item) => item.type === 'ATTACHMENT',
    ) as RegisterItemAttachmentProperty<string>[]
  }

  public getInitialTabName(columnId: string): string {
    // multiple inspections column ids are possible now. they should all open the inspections tab
    if (columnId?.includes('inspection')) return 'inspections'
    if (columnId === 'permit') return 'permits'
    if (columnId === 'task') return 'tasks'
    if (columnId === 'checklist') return 'checklists'
    const isAttachmentColumn = this.data.some(
      (property) =>
        property.registerPropertyId === columnId &&
        property.type === 'ATTACHMENT',
    )
    if (isAttachmentColumn) return 'attachments'
    return 'properties'
  }
}
